import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { AUTO_DISMISS_MS } from '../ui/Toast';
import { useTargetIntel } from '../../hooks/useTargetIntel';
import { isProtected } from '../../utils/protection';
import { ProtectedBadge } from '../ProtectedBadge';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { MobileCrystalBallCard } from './MobileCrystalBallCard';
import { MobileSpyOnMilitaryCard } from './MobileSpyOnMilitaryCard';
import { MobileSurveyCard } from './MobileSurveyCard';
import type { ProvinceSnapshotDto } from '../../api/intel';

// Matches SiblingRow.tsx's own rendered height: 44px minHeight (its Link children) + 1px
// borderBottom — same precedent as MobileWarRoomShell.tsx's SIBLING_ROW_HEIGHT_PX.
const SIBLING_ROW_HEIGHT_PX = 45;
// Rendered height of this view's own sticky action bar (below), for the scroll-content padding
// reserve — the CLAUDE.md SF-132 pattern: an offset alone doesn't reserve scroll space.
const ACTION_BAR_HEIGHT_PX = 64;
// MobileTabBar's own reserved bottom space on every Realm/War/Kingdom/World sibling route.
const TAB_BAR_OFFSET_PX = 96;

// SF-125: derives the banner's alpha-blended background from the COLORS.positive/negative tokens
// themselves, rather than hand-copying a decimal-RGB literal — a literal restatement silently
// desyncs the instant the token's hex value changes (the exact hazard this feature's own
// Component 8 decision cites for using tokens over Toast.tsx's private TOAST_STYLES literals).
function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// SF-181: 'warning' (a fizzle) is distinct from 'failure' (a genuine response.error) — amber,
// not red, matching Toast.tsx's own warning/failure distinction.
function bannerColor(type: 'success' | 'failure' | 'warning'): string {
  if (type === 'success') return COLORS.positive;
  if (type === 'warning') return COLORS.warning;
  return COLORS.negative;
}

interface MobileTargetDetailViewProps {
  worldId: string;
  provinceId: string;
  target: ProvinceSnapshotDto;
  currentTick: number;
  onBack: () => void;
}

/**
 * SF-171 Component 8: renders in place of the list when a target is selected. Chrome
 * (SiblingRow/MobileTabBar) never unmounts — this is real navigable URL state (`?target=`), not
 * local component state, so system/hardware back and browser refresh both work.
 */
export function MobileTargetDetailView({ worldId, provinceId, target, currentTick, onBack }: MobileTargetDetailViewProps) {
  const navigate = useNavigate();
  const h1Ref = useRef<HTMLHeadingElement>(null);
  // SF-181: 'warning' added for useGatherIntel's Crystal Ball fizzle tone (was 'failure').
  const [banner, setBanner] = useState<{ message: string; type: 'success' | 'failure' | 'warning' } | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { cbReport, somReport, surveyReport, cbData, somData, surveyData, isLoading, error, refreshIntel } =
    useTargetIntel(target.provinceId);

  const protectedTarget = isProtected(target.protectionExpiresAtTick, currentTick);
  const kdNwpa = target.kingdomLand > 0 ? Math.round(target.kingdomNw / target.kingdomLand) : 0;

  // Focus the heading on mount and on every target change (e.g. browser back/forward between two
  // targets) — extends the MobileKingdomViewerShell.tsx:55-61 mount-only convention.
  useEffect(() => {
    h1Ref.current?.focus();
  }, [target.provinceId]);

  function showResult(message: string, type: 'success' | 'failure' | 'warning') {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    setBanner({ message, type });
    dismissTimerRef.current = setTimeout(() => setBanner(null), AUTO_DISMISS_MS);
  }

  // Cleanup any pending auto-dismiss timer on unmount (e.g. a breakpoint flip mid-gather) so it
  // never fires setState after unmount.
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const queryStr = `?targetId=${target.provinceId}&targetName=${encodeURIComponent(target.provinceName)}&targetLoc=${encodeURIComponent(target.location)}`;

  return (
    <div style={{ paddingBottom: TAB_BAR_OFFSET_PX + ACTION_BAR_HEIGHT_PX + 16 }}>
      <div style={{ padding: '12px 12px 0' }}>
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to results"
          style={{ minHeight: 44, minWidth: 44, background: 'none', border: 'none', color: COLORS.link, fontSize: 14, cursor: 'pointer', padding: '4px 0' }}
        >
          ← Back
        </button>

        <h1 ref={h1Ref} tabIndex={-1} style={{ fontSize: 18, fontWeight: 700, color: COLORS.text.primary, margin: '4px 0 2px', outline: 'none' }}>
          {target.provinceName}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ color: COLORS.link, fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>({target.location})</span>
          {protectedTarget && <ProtectedBadge protectionExpiresAtTick={target.protectionExpiresAtTick} currentTick={currentTick} />}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12, color: COLORS.text.secondary, marginBottom: 10 }}>
          <span>{formatEnumLabel(target.race)}</span>
          <span>{formatEnumLabel(target.rank)}</span>
          <span>Land (acres) {formatNumber(target.land)}</span>
          <span style={{ color: COLORS.gold }}>NW (gc) {formatNumber(target.networth)}</span>
        </div>

        <div style={{ background: COLORS.cardAlt, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
          <div style={{ color: COLORS.text.tertiary, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
            Kingdom
          </div>
          <div style={{ color: COLORS.text.primary, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{target.kingdomName}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 11, color: COLORS.text.secondary }}>
            <span>Land (acres) {formatNumber(target.kingdomLand)}</span>
            <span>NW (gc) {formatNumber(target.kingdomNw)}</span>
            <span>NW/Acre {formatNumber(kdNwpa)}</span>
          </div>
        </div>
      </div>

      {banner && (
        <div
          role="alert"
          style={{
            position: 'sticky',
            top: SIBLING_ROW_HEIGHT_PX,
            zIndex: 10,
            margin: '0 12px 10px',
            padding: '10px 12px',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: hexToRgba(bannerColor(banner.type), 0.12),
            border: `1px solid ${bannerColor(banner.type)}`,
            color: COLORS.text.primary,
            fontSize: 13,
          }}
        >
          <span style={{ flex: 1 }}>{banner.message}</span>
          <button
            type="button"
            onClick={() => setBanner(null)}
            aria-label="Dismiss"
            style={{ background: 'none', border: 'none', color: COLORS.text.tertiary, fontSize: 16, cursor: 'pointer', padding: '2px' }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{ padding: '0 12px' }}>
        <MobileCrystalBallCard
          worldId={worldId}
          playerProvinceId={provinceId}
          targetProvinceId={target.provinceId}
          data={cbData}
          report={cbReport}
          isLoading={isLoading}
          error={error}
          onResult={showResult}
          onGathered={refreshIntel}
        />
        <MobileSpyOnMilitaryCard
          worldId={worldId}
          playerProvinceId={provinceId}
          targetProvinceId={target.provinceId}
          data={somData}
          report={somReport}
          isLoading={isLoading}
          error={error}
          disabled={protectedTarget}
          onResult={showResult}
          onGathered={refreshIntel}
        />
        <MobileSurveyCard
          worldId={worldId}
          playerProvinceId={provinceId}
          targetProvinceId={target.provinceId}
          data={surveyData}
          report={surveyReport}
          isLoading={isLoading}
          error={error}
          disabled={protectedTarget}
          onResult={showResult}
          onGathered={refreshIntel}
        />
      </div>

      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: TAB_BAR_OFFSET_PX,
          zIndex: 30,
          padding: '8px 12px',
          display: 'flex',
          gap: 8,
          background: COLORS.bg,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <button
          type="button"
          onClick={() => navigate(`/attack${queryStr}`)}
          style={{ flex: 2, minHeight: 44, borderRadius: 6, border: 'none', background: COLORS.btnPrimary, color: '#e8ecf4', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >
          War Room
        </button>
        <button
          type="button"
          onClick={() => navigate(`/thievery${queryStr}`)}
          style={{ flex: 1, minHeight: 44, borderRadius: 6, background: 'transparent', color: '#c4b0e8', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(107,79,160,0.4)' }}
        >
          Thievery
        </button>
        <button
          type="button"
          onClick={() => navigate(`/mystics${queryStr}`)}
          style={{ flex: 1, minHeight: 44, borderRadius: 6, background: 'transparent', color: COLORS.link, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(107,144,224,0.3)' }}
        >
          Magic
        </button>
      </div>
    </div>
  );
}
